/**
 * Cookie utilities for client-side and server-side cookie operations
 * Provides SSR-safe cookie reading and writing with proper TypeScript types
 *
 * Example usage:
 *
 * // String cookies
 * setCookie('theme', 'dark')
 * const theme = getCookie('theme') // 'dark'
 *
 * // Numeric cookies
 * setNumericCookie('panel_width', 25)
 * const width = getNumericCookie('panel_width') // 25
 *
 * // Boolean cookies
 * setBooleanCookie('sidebar_collapsed', true)
 * const collapsed = getBooleanCookie('sidebar_collapsed') // true
 *
 * // JSON cookies
 * setJsonCookie('user_preferences', { theme: 'dark', language: 'en' })
 * const prefs = getJsonCookie<{ theme: string; language: string }>('user_preferences')
 *
 * // Server-side usage (in page.tsx)
 * const cookieStore = await cookies()
 * const cookieString = cookieStore.toString()
 * const width = getNumericCookieFromString(cookieString, 'panel_width')
 */

// Cookie configuration
const COOKIE_CONFIG = {
  path: '/',
  maxAge: 31536000, // 1 year in seconds
  sameSite: 'Lax' as const,
} as const

/**
 * Set a cookie with standard configuration
 * @param name - Cookie name
 * @param value - Cookie value
 * @param options - Optional cookie configuration
 */
export const setCookie = (
  name: string,
  value: string,
  options: Partial<{
    maxAge: number
    path: string
    sameSite: 'Lax' | 'Strict' | 'None'
    secure: boolean
  }> = {}
): void => {
  if (typeof window === 'undefined') return

  const {
    maxAge = COOKIE_CONFIG.maxAge,
    path = COOKIE_CONFIG.path,
    sameSite = COOKIE_CONFIG.sameSite,
    secure = false,
  } = options

  const cookieValue = `${name}=${encodeURIComponent(value)}; path=${path}; max-age=${maxAge}; SameSite=${sameSite}`
  const secureCookie = secure ? `${cookieValue}; Secure` : cookieValue

  document.cookie = secureCookie
}

/**
 * Get a cookie value by name
 * @param name - Cookie name
 * @returns Cookie value or undefined if not found
 */
export const getCookie = (name: string): string | undefined => {
  if (typeof window === 'undefined') return undefined

  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Get a cookie value by name from a cookie string (server-side compatible)
 * @param cookieString - Raw cookie string from headers
 * @param name - Cookie name
 * @returns Cookie value or undefined if not found
 */
export const getCookieFromString = (cookieString: string, name: string): string | undefined => {
  const match = cookieString.match(new RegExp(`(?:^|; )${name}=([^;]*)`))
  return match ? decodeURIComponent(match[1]) : undefined
}

/**
 * Delete a cookie by setting it to expire in the past
 * @param name - Cookie name
 * @param path - Cookie path (must match the path used when setting)
 */
export const deleteCookie = (name: string, path: string = '/'): void => {
  if (typeof window === 'undefined') return

  document.cookie = `${name}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`
}

/**
 * Get a numeric cookie value
 * @param name - Cookie name
 * @returns Numeric value or undefined if not found or invalid
 */
export const getNumericCookie = (name: string): number | undefined => {
  const value = getCookie(name)
  if (value) {
    const num = parseFloat(value)
    return isNaN(num) ? undefined : num
  }
  return undefined
}

/**
 * Get a numeric cookie value from a cookie string (server-side compatible)
 * @param cookieString - Raw cookie string from headers
 * @param name - Cookie name
 * @returns Numeric value or undefined if not found or invalid
 */
export const getNumericCookieFromString = (
  cookieString: string,
  name: string
): number | undefined => {
  const value = getCookieFromString(cookieString, name)
  if (value) {
    const num = parseFloat(value)
    return isNaN(num) ? undefined : num
  }
  return undefined
}

/**
 * Set a numeric cookie value
 * @param name - Cookie name
 * @param value - Numeric value to store
 * @param options - Optional cookie configuration
 */
export const setNumericCookie = (
  name: string,
  value: number,
  options?: Parameters<typeof setCookie>[2]
): void => {
  setCookie(name, value.toString(), options)
}

/**
 * Get a boolean cookie value
 * @param name - Cookie name
 * @returns Boolean value or undefined if not found
 */
export const getBooleanCookie = (name: string): boolean | undefined => {
  const value = getCookie(name)
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

/**
 * Get a boolean cookie value from a cookie string (server-side compatible)
 * @param cookieString - Raw cookie string from headers
 * @param name - Cookie name
 * @returns Boolean value or undefined if not found
 */
export const getBooleanCookieFromString = (
  cookieString: string,
  name: string
): boolean | undefined => {
  const value = getCookieFromString(cookieString, name)
  if (value === 'true') return true
  if (value === 'false') return false
  return undefined
}

/**
 * Set a boolean cookie value
 * @param name - Cookie name
 * @param value - Boolean value to store
 * @param options - Optional cookie configuration
 */
export const setBooleanCookie = (
  name: string,
  value: boolean,
  options?: Parameters<typeof setCookie>[2]
): void => {
  setCookie(name, value.toString(), options)
}

/**
 * Get a JSON cookie value
 * @param name - Cookie name
 * @returns Parsed JSON value or undefined if not found or invalid
 */
export const getJsonCookie = <T>(name: string): T | undefined => {
  const value = getCookie(name)
  if (value) {
    try {
      return JSON.parse(value) as T
    } catch {
      return undefined
    }
  }
  return undefined
}

/**
 * Get a JSON cookie value from a cookie string (server-side compatible)
 * @param cookieString - Raw cookie string from headers
 * @param name - Cookie name
 * @returns Parsed JSON value or undefined if not found or invalid
 */
export const getJsonCookieFromString = <T>(cookieString: string, name: string): T | undefined => {
  const value = getCookieFromString(cookieString, name)
  if (value) {
    try {
      return JSON.parse(value) as T
    } catch {
      return undefined
    }
  }
  return undefined
}

/**
 * Set a JSON cookie value
 * @param name - Cookie name
 * @param value - JSON value to store
 * @param options - Optional cookie configuration
 */
export const setJsonCookie = (
  name: string,
  value: unknown,
  options?: Parameters<typeof setCookie>[2]
): void => {
  setCookie(name, JSON.stringify(value), options)
}
